import { c as _c } from "react/compiler-runtime";
import { useRequestErrorToast } from '@onefootprint/hooks';
import { useRequestError } from '@onefootprint/request';
import type { AuthMethodKind, ChallengeKind, IdentifyRequirementChallenge } from '@onefootprint/request-types';
import { Divider } from '@onefootprint/ui';
import { useEffect, useReducer, useState } from 'react';
import { getLogger } from '@/idv/utils';
import LegalFooter, { usePostTermsAndConditionsConsentMutation } from '../legal-footer';
import Challenge from './components/challenge';
import CollectEmail from './components/collect-email';
import CollectPhone from './components/collect-phone';
import DifferentAccountOption from './components/different-account-option';
import Init from './components/init';
import LinkExpiredError from './components/link-expired-error';
import LoginChallenge from './components/login-challenge/login-challenge';
import type { DoneArgs, InitArgs } from './identify.types';
import loadNextRequirement from './utils/load-requirements';
import { getInitialState, type NextAction, reducer, wasPasskeyRegistered } from './utils/reducer';
const {
  logWarn
} = getLogger({
  location: 'identify'
});
type IdentifyProps = {
  onDone: (args: DoneArgs) => void;
  onBackButtonVisibilityChange?: (canGoBack: boolean) => void;
  onExternalBack?: (cb: () => void) => () => void;
  initArgs: InitArgs;
};
type AdvanceArgs = Partial<Omit<NextAction, 'type'>> & {
  delayMs?: number;
  displayedTerms?: boolean;
};
const Identify = t0 => {
  const $ = _c(110);
  const {
    onDone,
    onBackButtonVisibilityChange,
    onExternalBack,
    initArgs: t1
  } = t0;
  let bootstrapData;
  let initArgs;
  if ($[0] !== t1) {
    ({
      bootstrapData,
      ...initArgs
    } = t1);
    $[0] = t1;
    $[1] = bootstrapData;
    $[2] = initArgs;
  } else {
    bootstrapData = $[1];
    initArgs = $[2];
  }
  let t2;
  if ($[3] !== bootstrapData) {
    t2 = getInitialState(bootstrapData);
    $[3] = bootstrapData;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const initialState = t2;
  const [state, dispatch] = useReducer(reducer, initialState);
  const showRequestErrorToast = useRequestErrorToast();
  const {
    getErrorCode
  } = useRequestError();
  const [isExpiredError, setIsExpiredError] = useState(false);
  const postTermsAndConditionsConsentMutation = usePostTermsAndConditionsConsentMutation(state.identifyToken);
  let t3;
  if ($[5] !== onBackButtonVisibilityChange || $[6] !== onDone || $[7] !== postTermsAndConditionsConsentMutation || $[8] !== state) {
    t3 = async t4 => {
      const {
        delayMs,
        displayedTerms,
        ...args
      } = t4;
      const identifyToken = args.identifyToken || state.identifyToken;
      if (displayedTerms) {
        await postTermsAndConditionsConsentMutation.mutateAsync();
      }
      const nextRequirement = await loadNextRequirement(identifyToken);
      const next = () => {
        if (nextRequirement.kind === "handleNextRequirement") {
          dispatch({
            type: "next",
            ...args,
            ...nextRequirement
          });
        } else {
          if (nextRequirement.kind === "done") {
            const {
              email,
              phoneNumber
            } = state;
            const isPasskeyRegistered = wasPasskeyRegistered(state);
            onBackButtonVisibilityChange?.(false);
            onDone({
              authToken: nextRequirement.authToken,
              email,
              phoneNumber,
              isPasskeyRegistered
            });
          }
        }
      };
      if (delayMs) {
        setTimeout(next, delayMs);
      } else {
        next();
      }
    };
    $[5] = onBackButtonVisibilityChange;
    $[6] = onDone;
    $[7] = postTermsAndConditionsConsentMutation;
    $[8] = state;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleAdvanceToNext = t3;
  let t4;
  if ($[10] !== state.disableReset) {
    t4 = state.disableReset ? undefined : () => {
      dispatch({
        type: "resetToLoginWithDifferentAccount"
      });
    };
    $[10] = state.disableReset;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleReset = t4;
  let t5;
  if ($[12] !== getErrorCode || $[13] !== showRequestErrorToast) {
    t5 = (location, error) => {
      logWarn(`Error in ${location}:`, error);
      showRequestErrorToast(error);
      const errorCode = getErrorCode(error);
      const isExpiredError_0 = errorCode === "E118";
      if (isExpiredError_0) {
        setIsExpiredError(true);
      }
    };
    $[12] = getErrorCode;
    $[13] = showRequestErrorToast;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const onError = t5;
  const canGoBack = state.requirementHistory.length > 0;
  let t6;
  let t7;
  if ($[15] !== canGoBack || $[16] !== onBackButtonVisibilityChange) {
    t6 = () => {
      onBackButtonVisibilityChange?.(canGoBack);
    };
    t7 = [canGoBack, onBackButtonVisibilityChange];
    $[15] = canGoBack;
    $[16] = onBackButtonVisibilityChange;
    $[17] = t6;
    $[18] = t7;
  } else {
    t6 = $[17];
    t7 = $[18];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[19] !== canGoBack || $[20] !== onExternalBack) {
    t8 = () => {
      if (!onExternalBack || !canGoBack) {
        return;
      }
      return onExternalBack(() => dispatch({
        type: "prev"
      }));
    };
    t9 = [onExternalBack, canGoBack];
    $[19] = canGoBack;
    $[20] = onExternalBack;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  useEffect(t8, t9);
  let t10;
  if ($[23] !== canGoBack) {
    t10 = canGoBack ? () => dispatch({
      type: "prev"
    }) : undefined;
    $[23] = canGoBack;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const onPrev = t10;
  if (isExpiredError) {
    let t11;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <LinkExpiredError />;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    return t11;
  }
  const {
    requirement,
    identifyToken: identifyToken_0,
    displayTermsAndConditions
  } = state;
  let t11;
  if ($[26] !== initArgs || $[27] !== onError || $[28] !== state) {
    t11 = {
      state,
      onError,
      initArgs
    };
    $[26] = initArgs;
    $[27] = onError;
    $[28] = state;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const context = t11;
  if (!identifyToken_0 || !requirement) {
    const t12 = state.email?.value;
    const t13 = state.email?.isBootstrap;
    let t14;
    if ($[30] !== t12 || $[31] !== t13) {
      t14 = {
        value: t12,
        isBootstrap: t13
      };
      $[30] = t12;
      $[31] = t13;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    const t15 = state.phoneNumber?.value;
    const t16 = state.phoneNumber?.isBootstrap;
    let t17;
    if ($[33] !== t15 || $[34] !== t16) {
      t17 = {
        value: t15,
        isBootstrap: t16
      };
      $[33] = t15;
      $[34] = t16;
      $[35] = t17;
    } else {
      t17 = $[35];
    }
    let t18;
    if ($[36] !== t14 || $[37] !== t17) {
      t18 = {
        email: t14,
        phoneNumber: t17
      };
      $[36] = t14;
      $[37] = t17;
      $[38] = t18;
    } else {
      t18 = $[38];
    }
    let t19;
    if ($[39] !== handleAdvanceToNext) {
      t19 = identifyToken_1 => handleAdvanceToNext({
        identifyToken: identifyToken_1
      });
      $[39] = handleAdvanceToNext;
      $[40] = t19;
    } else {
      t19 = $[40];
    }
    let t20;
    if ($[41] !== context || $[42] !== onError || $[43] !== state.bootstrapData || $[44] !== t18 || $[45] !== t19) {
      t20 = <Init bootstrapData={state.bootstrapData} defaultValues={t18} context={context} onDone={t19} onError={onError} />;
      $[41] = context;
      $[42] = onError;
      $[43] = state.bootstrapData;
      $[44] = t18;
      $[45] = t19;
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    return t20;
  }
  if (requirement.kind === "collect_data" && requirement.cdo === "email") {
    const t12 = state.email?.value;
    let t13;
    if ($[47] !== t12) {
      t13 = {
        email: t12
      };
      $[47] = t12;
      $[48] = t13;
    } else {
      t13 = $[48];
    }
    let t14;
    if ($[49] !== displayTermsAndConditions || $[50] !== handleAdvanceToNext) {
      t14 = email_0 => handleAdvanceToNext({
        email: email_0,
        displayedTerms: displayTermsAndConditions
      });
      $[49] = displayTermsAndConditions;
      $[50] = handleAdvanceToNext;
      $[51] = t14;
    } else {
      t14 = $[51];
    }
    let t15;
    if ($[52] !== displayTermsAndConditions) {
      t15 = displayTermsAndConditions ? <LegalFooter /> : undefined;
      $[52] = displayTermsAndConditions;
      $[53] = t15;
    } else {
      t15 = $[53];
    }
    let t16;
    if ($[54] !== context || $[55] !== onError || $[56] !== onPrev || $[57] !== t13 || $[58] !== t14 || $[59] !== t15) {
      t16 = <CollectEmail defaultValues={t13} context={context} onPrev={onPrev} onDone={t14} onError={onError}>{t15}</CollectEmail>;
      $[54] = context;
      $[55] = onError;
      $[56] = onPrev;
      $[57] = t13;
      $[58] = t14;
      $[59] = t15;
      $[60] = t16;
    } else {
      t16 = $[60];
    }
    return t16;
  }
  if (requirement.kind === "collect_data" && requirement.cdo === "phone_number") {
    const t12 = state.phoneNumber?.value;
    let t13;
    if ($[61] !== t12) {
      t13 = {
        phoneNumber: t12
      };
      $[61] = t12;
      $[62] = t13;
    } else {
      t13 = $[62];
    }
    let t14;
    if ($[63] !== displayTermsAndConditions || $[64] !== handleAdvanceToNext) {
      t14 = phoneNumber_0 => handleAdvanceToNext({
        phoneNumber: phoneNumber_0,
        displayedTerms: displayTermsAndConditions
      });
      $[63] = displayTermsAndConditions;
      $[64] = handleAdvanceToNext;
      $[65] = t14;
    } else {
      t14 = $[65];
    }
    let t15;
    if ($[66] !== displayTermsAndConditions) {
      t15 = displayTermsAndConditions ? <LegalFooter /> : undefined;
      $[66] = displayTermsAndConditions;
      $[67] = t15;
    } else {
      t15 = $[67];
    }
    let t16;
    if ($[68] !== context || $[69] !== onError || $[70] !== onPrev || $[71] !== t13 || $[72] !== t14 || $[73] !== t15) {
      t16 = <CollectPhone defaultValues={t13} context={context} onPrev={onPrev} onDone={t14} onError={onError}>{t15}</CollectPhone>;
      $[68] = context;
      $[69] = onError;
      $[70] = onPrev;
      $[71] = t13;
      $[72] = t14;
      $[73] = t15;
      $[74] = t16;
    } else {
      t16 = $[74];
    }
    return t16;
  }
  if (requirement.kind === "challenge") {
    const t12 = state.email?.value;
    const t13 = state.phoneNumber?.value;
    let t14;
    if ($[75] !== t12 || $[76] !== t13) {
      t14 = {
        email: t12,
        phoneNumber: t13
      };
      $[75] = t12;
      $[76] = t13;
      $[77] = t14;
    } else {
      t14 = $[77];
    }
    let t15;
    if ($[78] !== requirement) {
      t15 = getChallengeKind(requirement);
      $[78] = requirement;
      $[79] = t15;
    } else {
      t15 = $[79];
    }
    let t16;
    if ($[80] !== displayTermsAndConditions || $[81] !== handleAdvanceToNext) {
      t16 = delayMs_0 => handleAdvanceToNext({
        delayMs: delayMs_0,
        displayedTerms: displayTermsAndConditions
      });
      $[80] = displayTermsAndConditions;
      $[81] = handleAdvanceToNext;
      $[82] = t16;
    } else {
      t16 = $[82];
    }
    let t17;
    if ($[83] !== context || $[84] !== handleReset) {
      t17 = <DifferentAccountOption onLoginWithDifferentAccount={handleReset} context={context} />;
      $[83] = context;
      $[84] = handleReset;
      $[85] = t17;
    } else {
      t17 = $[85];
    }
    let t18;
    if ($[86] !== displayTermsAndConditions) {
      t18 = !displayTermsAndConditions ? null : <div className="flex flex-col items-center justify-center gap-8 mt-4"><Divider /><LegalFooter /></div>;
      $[86] = displayTermsAndConditions;
      $[87] = t18;
    } else {
      t18 = $[87];
    }
    let t19;
    if ($[88] !== context || $[89] !== onError || $[90] !== onPrev || $[91] !== requirement.authMethod || $[92] !== t14 || $[93] !== t15 || $[94] !== t16 || $[95] !== t17 || $[96] !== t18) {
      t19 = <Challenge context={context} defaultValues={t14} challengeKind={t15} onDone={t16} onPrev={onPrev} onError={onError} key={requirement.authMethod}>{t17}{t18}</Challenge>;
      $[88] = context;
      $[89] = onError;
      $[90] = onPrev;
      $[91] = requirement.authMethod;
      $[92] = t14;
      $[93] = t15;
      $[94] = t16;
      $[95] = t17;
      $[96] = t18;
      $[97] = t19;
    } else {
      t19 = $[97];
    }
    return t19;
  }
  if (requirement.kind === "login") {
    let t12;
    if ($[98] !== handleAdvanceToNext || $[99] !== state.displayTermsAndConditions) {
      t12 = delayMs_1 => handleAdvanceToNext({
        delayMs: delayMs_1,
        displayedTerms: state.displayTermsAndConditions
      });
      $[98] = handleAdvanceToNext;
      $[99] = state.displayTermsAndConditions;
      $[100] = t12;
    } else {
      t12 = $[100];
    }
    let t13;
    if ($[101] !== displayTermsAndConditions) {
      t13 = !displayTermsAndConditions ? null : <div className="flex flex-col items-center justify-center gap-8"><Divider /><LegalFooter /></div>;
      $[101] = displayTermsAndConditions;
      $[102] = t13;
    } else {
      t13 = $[102];
    }
    let t14;
    if ($[103] !== context || $[104] !== handleReset || $[105] !== onPrev || $[106] !== requirement || $[107] !== t12 || $[108] !== t13) {
      t14 = <LoginChallenge context={context} requirement={requirement} onDone={t12} onPrev={onPrev} onReset={handleReset}>{t13}</LoginChallenge>;
      $[103] = context;
      $[104] = handleReset;
      $[105] = onPrev;
      $[106] = requirement;
      $[107] = t12;
      $[108] = t13;
      $[109] = t14;
    } else {
      t14 = $[109];
    }
    return t14;
  }
};
const getChallengeKind = (requirement: IdentifyRequirementChallenge) => {
  if (requirement.challengeKinds.includes('sna')) {
    // Prefer running SNA if it exists
    return 'sna';
  }
  const challengeKindForAuthMethod: Record<AuthMethodKind, ChallengeKind> = {
    email: 'email',
    phone: 'sms',
    passkey: 'biometric'
  };
  return challengeKindForAuthMethod[requirement.authMethod];
};
export default Identify;